import { useCallback, useEffect, useRef, useState } from "react";
import { ActionButton } from "../../../components/Buttons";
import LettuceLogo from "../../../images/logo.png";
import {
  ACCOUNT_LINKING_RESULT_OUTCOMES,
  ACCOUNT_LINKING_STATUS_MESSAGES,
  handleGoogleAccountLinkingResult,
} from "../../../utils/auth/accountLinkingResult";
import {
  GOOGLE_AUTH_OUTCOMES,
  linkAnonymousUserWithGoogle,
  signInWithGoogle,
} from "../../../utils/auth/googleAuth";
import { trackMixpanelEvent } from "../../../utils/Analytics";
import {
  getAccountSessionRecoveryState,
} from "../../../utils/auth/accountSessionRecovery";

const ACCOUNT_LINKING_COPY = Object.freeze({
  "keep-cards": {
    eyebrow: "",
    title: "Create Your LettuceTalk Account",
    body:
      "Create an account to save the cards you made and make more cards.",
    primary: "Sign in with Google",
    secondary: "Not now",
  },
  "create-another": {
    eyebrow: "",
    title: "Sign up or log in to make more cards",
    body:
      "You've already used your free anonymous card set! Please create an account to create more and come back to your cards later.",
    primary: "Sign in with Google",
    secondary: "",
  },
});

function GoogleAccountButton({
  disabled,
  isLinking,
  label,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[48px] w-full items-center justify-center gap-3 rounded-[999px] border border-[#dadce0] bg-white px-4 py-2 text-sm font-medium leading-5 text-[#3c4043] shadow-sm transition hover:bg-[#f8fafd] hover:shadow disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80"
    >
      {isLinking ? (
        <span
          aria-hidden="true"
          className="loading loading-spinner loading-sm text-[#3c4043]"
        />
      ) : (
        <svg
          aria-hidden="true"
          className="h-[18px] w-[18px] shrink-0"
          viewBox="0 0 18 18"
        >
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.33-1.58-5.04-3.72H.94v2.34A9 9 0 0 0 9 18Z"
          />
          <path
            fill="#FBBC05"
            d="M3.96 10.7A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.28-1.7V4.96H.94A9 9 0 0 0 0 9c0 1.45.34 2.82.94 4.04l3.02-2.34Z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .94 4.96L3.96 7.3C4.67 5.16 6.66 3.58 9 3.58Z"
          />
        </svg>
      )}
      <span>{isLinking ? "Connecting..." : label}</span>
    </button>
  );
}

function AccountLinkingSheet({
  isOpen,
  intent = "keep-cards",
  initialFallbackConflict = false,
  mergeContext,
  sessionSave = false,
  sessionIntent,
  recoveryOnly = false,
  onDismiss,
  onLinked,
  onConflict,
}) {
  const [statusMessage, setStatusMessage] = useState("");
  const [isLinking, setIsLinking] = useState(false);
  const [isFallbackConflict, setIsFallbackConflict] = useState(false);
  const [recoveryAction, setRecoveryAction] = useState("");
  const isLinkingRef = useRef(false);
  const trackedPromptRef = useRef(false);
  const recoveryAttemptedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setStatusMessage("");
      setIsLinking(false);
      setIsFallbackConflict(false);
      setRecoveryAction("");
      isLinkingRef.current = false;
      trackedPromptRef.current = false;
      recoveryAttemptedRef.current = false;
      return;
    }

    if (!trackedPromptRef.current) {
      trackedPromptRef.current = true;
      trackMixpanelEvent("Account Prompt Viewed", {
        prompt_intent: intent,
        user_state: "anonymous",
      });
    }

    setStatusMessage("");
    if (initialFallbackConflict) {
      setStatusMessage(ACCOUNT_LINKING_STATUS_MESSAGES.missingMergeSource);
    }
    setIsFallbackConflict(initialFallbackConflict);
  }, [initialFallbackConflict, intent, isOpen]);

  const applyResult = useCallback((result) => {
    setStatusMessage(result.statusMessage || "");
    setIsFallbackConflict(
      result.outcome === ACCOUNT_LINKING_RESULT_OUTCOMES.FALLBACK_CONFLICT
    );
    setRecoveryAction(result.recoveryAction || "");
  }, []);

  const handleResumeRecovery = useCallback(async () => {
    if (isLinkingRef.current) {
      return;
    }

    isLinkingRef.current = true;
    setIsLinking(true);
    setStatusMessage("");

    try {
      const result = await handleGoogleAccountLinkingResult({
        onLinked,
        recoveryOnly: true,
      });
      applyResult(result);
    } catch {
      setStatusMessage(ACCOUNT_LINKING_STATUS_MESSAGES.sessionRedemptionFailure);
      setRecoveryAction("retry");
    } finally {
      isLinkingRef.current = false;
      setIsLinking(false);
    }
  }, [applyResult, onLinked]);

  useEffect(() => {
    if (!isOpen || !recoveryOnly || recoveryAttemptedRef.current) {
      return;
    }

    recoveryAttemptedRef.current = true;
    handleResumeRecovery();
  }, [handleResumeRecovery, isOpen, recoveryOnly]);

  if (!isOpen) {
    return null;
  }

  const copy = ACCOUNT_LINKING_COPY[intent] || ACCOUNT_LINKING_COPY["keep-cards"];

  const handleDismiss = () => {
    if (isLinkingRef.current) {
      return;
    }

    onDismiss?.();
  };

  const handleLinkWithGoogle = async () => {
    if (isLinkingRef.current) {
      return;
    }

    isLinkingRef.current = true;
    setIsLinking(true);
    setStatusMessage("");
    setIsFallbackConflict(false);
    setRecoveryAction("");

    try {
      const authResult = await linkAnonymousUserWithGoogle();

      const result = await handleGoogleAccountLinkingResult({
        authResult,
        mergeContext,
        sessionSave,
        sessionIntent,
        onLinked,
        onConflict,
      });

      applyResult(result);
    } catch {
      setStatusMessage(ACCOUNT_LINKING_STATUS_MESSAGES.failure);
    } finally {
      isLinkingRef.current = false;
      setIsLinking(false);
    }
  };

  const handleRecoverySignIn = async () => {
    if (isLinkingRef.current) {
      return;
    }

    isLinkingRef.current = true;
    setIsLinking(true);
    setStatusMessage("");

    try {
      const authResult = await signInWithGoogle();

      if (authResult?.status === GOOGLE_AUTH_OUTCOMES.CANCELLED) {
        return;
      }

      if (authResult?.status !== GOOGLE_AUTH_OUTCOMES.SUCCESS) {
        setStatusMessage(ACCOUNT_LINKING_STATUS_MESSAGES.sessionNeedsSignIn);
        setRecoveryAction("sign-in");
        return;
      }

      const result = await handleGoogleAccountLinkingResult({
        authResult,
        expectedUid: authResult?.uid,
        onLinked,
        recoveryOnly: true,
        sessionIntent,
      });
      applyResult(result);
    } catch {
      setStatusMessage(ACCOUNT_LINKING_STATUS_MESSAGES.sessionNeedsSignIn);
      setRecoveryAction("sign-in");
    } finally {
      isLinkingRef.current = false;
      setIsLinking(false);
    }
  };

  const handlePrimaryAction = () => {
    const recoveryState = getAccountSessionRecoveryState();

    if (recoveryAction === "sign-in") {
      handleRecoverySignIn();
      return;
    }

    if (recoveryState?.destinationUid || recoveryOnly) {
      handleResumeRecovery();
      return;
    }

    handleLinkWithGoogle();
  };

  const handleUseAnotherGoogleAccount = () => {
    handleLinkWithGoogle();
  };

  const isConflict = isFallbackConflict;
  const hasRecovery = Boolean(
    getAccountSessionRecoveryState()?.tickets?.length
  );

  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/62 px-4 pb-4 pt-10 backdrop-blur-sm sm:items-center sm:pb-10">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-linking-sheet-title"
        className="relative w-full max-w-md animate-in fade-in slide-in-from-bottom-4 rounded-[1.4rem] border border-white/14 bg-[#14170f] p-5 text-center shadow-[0_28px_90px_rgba(0,0,0,0.5)] sm:p-6"
      >
        <div>
          <div className="mx-auto max-w-xs">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-[#f4d94e]/10 shadow-[0_0_34px_rgba(244,217,78,0.28),inset_0_1px_0_rgba(244,217,78,0.16)]">
              <img
                src={LettuceLogo}
                alt="LettuceTalk"
                className="h-16 w-16 object-contain p-1.5"
              />
            </div>

            {copy.eyebrow ? (
              <p className="text-[11px] font-black uppercase text-[#f4d94e]/72">
                {copy.eyebrow}
              </p>
            ) : null}
            <h2
              id="account-linking-sheet-title"
              className="mt-1 font-serif text-2xl font-medium leading-tight text-white"
            >
              {copy.title}
            </h2>
          </div>

          <ActionButton
            type="button"
            onClick={handleDismiss}
            className="absolute right-5 top-5 text-white"
            disabled={isLinking}
            icon="x"
            iconClassName="h-5 w-5 shrink-0 text-white"
            size="icon"
            variant="darkSecondary"
            aria-label="Close account linking"
          />
        </div>

        {isConflict ? null : (
          <p className="mt-4 text-sm font-semibold leading-6 text-white/70">
            {copy.body}
          </p>
        )}

        {statusMessage ? (
          <p
            role="status"
            className="mt-4 rounded-2xl border border-[#f4d94e]/18 bg-[#f4d94e]/10 px-4 py-3 text-sm font-bold leading-5 text-[#fff6b8]"
          >
            {statusMessage}
          </p>
        ) : null}

        <div className="mt-5 flex flex-col gap-3">
          {isConflict ? (
            <ActionButton
              type="button"
              onClick={handleUseAnotherGoogleAccount}
              disabled={isLinking}
              fullWidth
              variant="darkSecondary"
            >
              Use another Google account
            </ActionButton>
          ) : recoveryAction === "retry" && hasRecovery ? (
            <ActionButton
              type="button"
              onClick={handlePrimaryAction}
              disabled={isLinking}
              fullWidth
              variant="darkSecondary"
            >
              Try saving my cards again
            </ActionButton>
          ) : recoveryAction === "expired" ? null : (
            <GoogleAccountButton
              onClick={handlePrimaryAction}
              disabled={isLinking}
              isLinking={isLinking}
              label={
                recoveryAction === "sign-in"
                  ? "Sign in with Google to continue"
                  : copy.primary
              }
            />
          )}

          {copy.secondary ? (
            <ActionButton
              type="button"
              onClick={handleDismiss}
              disabled={isLinking}
              fullWidth
              className="text-white"
              variant="darkSecondary"
            >
              {copy.secondary}
            </ActionButton>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default AccountLinkingSheet;
